// Per-browser favorite pools, persisted in localStorage (no login, no PII —
// same spirit as the deviceId in api.ts). Stored as a JSON string array of
// poolIds under a single key. The key is namespaced per city
// (`${CITY.localStoragePrefix}:favorites`) so the DC and Arlington sites — which
// can share an apex/subdomain in the browser — never read each other's state.
import { CITY } from "./city";

const FAVORITES_KEY = `${CITY.localStoragePrefix}:favorites`;

/** Read the raw favorites array, tolerating missing/corrupt/duplicate values. */
function readFavorites(): string[] {
  const raw = localStorage.getItem(FAVORITES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Keep only strings and drop duplicates so a corrupt value can't break us.
    return Array.from(new Set(parsed.filter((v): v is string => typeof v === "string")));
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]): void {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

/** Current favorites as a Set for O(1) membership checks. */
export function getFavorites(): Set<string> {
  return new Set(readFavorites());
}

/** Is this pool currently favorited? */
export function isFavorite(poolId: string): boolean {
  return readFavorites().includes(poolId);
}

/** Flip a pool's favorite state; returns the new state (true = now favorited). */
export function toggleFavorite(poolId: string): boolean {
  const ids = readFavorites();
  const idx = ids.indexOf(poolId);
  if (idx === -1) {
    ids.push(poolId);
    writeFavorites(ids);
    return true;
  }
  ids.splice(idx, 1);
  writeFavorites(ids);
  return false;
}
